import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const partners = [
    { name: 'Construction Partner', logo: 'https://arcdom.b-cdn.net/partners/logo-serion-1.jpg' },
    { name: 'Architecture Firm', logo: 'https://arcdom.b-cdn.net/partners/PHOTO-2025-05-24-12-06-10.jpg' },
    { name: 'Design Studio', logo: 'https://arcdom.b-cdn.net/partners/PHOTO-2025-05-24-12-12-23.jpg' },
    { name: 'Building Materials', logo: 'https://arcdom.b-cdn.net/partners/format4.jpeg' },
    { name: 'Engineering Consultants', logo: 'https://arcdom.b-cdn.net/partners/PHOTO-2025-05-24-19-34-59.jpg' },
    { name: 'Quality Control', logo: 'https://arcdom.b-cdn.net/partners/PHOTO-2025-05-26-12-06-43.jpg' },
    { name: 'New Partner', logo: 'https://arcdom.b-cdn.net/partners/loco_profimat.jpg' }, // 7th partner
];

// Customize sizes for each specific logo based on index
const getCustomStyles = (index) => {
    const sizingMap = {
        0: { width: '180px', transform: 'scale(1.15)' },
        1: { width: '130px', transform: 'scale(1.15)' },
        2: { width: '210px' },
        3: { width: '90px', transform: 'scale(1.05)' },
        4: { width: '130px' },
        5: { width: '230px' },
        6: { width: '230px' }, // Styles for the 7th partner logo
    };
    return sizingMap[index] || { width: '100px' }; // Default size
};

const PartnerLogo = ({ partner, index }) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white p-5 rounded-lg shadow-md flex items-center justify-center h-40" // This is the visual card
        >
            <div className="flex items-center justify-center w-full h-full hover:scale-105 transition-transform duration-300">
                <img
                    src={partner.logo}
                    alt={partner.name}
                    style={getCustomStyles(index)}
                    className="object-contain max-h-full" // Ensure image respects container height
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src="https://placehold.co/150x80/f0f0f0/333?text=Logo+Error";
                        e.target.alt = `${partner.name} (logo error)`;
                    }}
                />
            </div>
        </motion.div>
    );
};

const Collaborators = () => {
    const [sectionRef, sectionInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <section id="collaborators" className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={sectionRef}
                    initial={{ opacity: 0, y: 20 }}
                    animate={sectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Trusted Partners</h2>
                    <div className="w-20 h-1 bg-yellow-500 mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        We collaborate with industry-leading partners to ensure the highest
                        quality standards in every project we undertake.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {partners.map((partner, index) => {
                        const isLastItem = index === partners.length - 1;
                        const numPartners = partners.length;

                        let wrapperClasses = [];
                        let needsWrapper = false;

                        if (isLastItem) {
                            // For LG screens (3 columns): if the last item is alone, span 3 cols and center content
                            if (numPartners % 3 === 1) {
                                wrapperClasses.push("lg:col-span-3", "lg:flex", "lg:justify-center");
                                needsWrapper = true;
                            }
                            // For MD screens (2 columns): if the last item is alone, span 2 cols and center content
                            if (numPartners % 2 === 1) {
                                // These classes will apply on MD. If LG classes were also added, Tailwind's breakpoint system
                                // ensures md: classes override for md viewport, and lg: classes for lg viewport.
                                wrapperClasses.push("md:col-span-2", "md:flex", "md:justify-center");
                                needsWrapper = true;
                            }
                        }

                        if (needsWrapper && wrapperClasses.length > 0) {
                            // Ensure unique classes, though with prefixes they target different breakpoints
                            const uniqueClasses = [...new Set(wrapperClasses)];
                            return (
                                <div key={`${partner.name}-${index}-wrapper`} className={uniqueClasses.join(" ")}>
                                    <PartnerLogo partner={partner} index={index} />
                                </div>
                            );
                        }

                        return <PartnerLogo key={`${partner.name}-${index}`} partner={partner} index={index} />;
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={sectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-center mt-16"
                >
                    <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                        Interested in Becoming a Partner?
                    </h3>
                    <p className="max-w-2xl mx-auto text-gray-600 mb-8">
                        We're always looking to work with quality-focused companies that share our values
                        and commitment to excellence.
                    </p>
                    <a
                        href="#contact"
                        className="inline-flex items-center px-6 py-3 bg-yellow-500 text-gray-800 rounded-md font-medium hover:bg-yellow-600 transition-all duration-300"
                    >
                        Get in Touch
                        <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default Collaborators;
